import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';

import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { metadatas } from '@sorare/core/src/lib/seo/football';
import { Matches } from '@sorare/gameplay/src/components/seo/Matches';

import { useLayoutData } from '#src/pages/leagues/$slug/layout.tsx';

import type {
  LeagueFixturesQuery,
  LeagueFixturesQueryVariables,
} from './__generated__/page.graphql.ts';

const LEAGUE_FIXTURES_QUERY = gql`
  query LeagueFixturesQuery(
    $slug: String!
    $cursor: String
    $futureCursor: String
  ) {
    football {
      competition(slug: $slug) {
        slug
        futureGames(first: 20, after: $futureCursor) {
          nodes {
            id
            ...Matches_anyGameInterface
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
        pastGames(first: 20, after: $cursor) {
          nodes {
            id
            ...Matches_anyGameInterface
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
      }
    }
  }
  ${Matches.fragments.anyGameInterface}
` as TypedDocumentNode<LeagueFixturesQuery, LeagueFixturesQueryVariables>;

const Fixtures = () => {
  const openDialog = useOpenDialog();
  const { slug, displayName } = useLayoutData();
  const [tab, setTab] = useState<'past' | 'future'>('future');
  useTitleAndDescription(
    metadatas.leagueFixtures.title,
    metadatas.leagueFixtures.description,
    {
      league: displayName,
    }
  );
  const { data, loading, loadMore } = usePaginatedQuery(LEAGUE_FIXTURES_QUERY, {
    variables: {
      slug: slug || '',
    },
    connection: 'GameConnection',
    skip: !slug,
  });
  const { futureGames, pastGames } = data?.football.competition || {};

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, {
        futureCursor: futureGames?.pageInfo.endCursor,
        cursor: pastGames?.pageInfo.endCursor,
      });
    },
    (tab === 'future' && !!futureGames?.pageInfo.hasNextPage) ||
      (tab === 'past' && !!pastGames?.pageInfo.hasNextPage),
    loading
  );

  if (tab === 'future' && (futureGames?.nodes || []).length <= 1 && !loading) {
    setTab('past');
  }

  return (
    <Matches
      displayName={displayName}
      loading={loading}
      pastGames={pastGames?.nodes || []}
      futureGames={futureGames?.nodes || []}
      onClick={id => openDialog(DialogKey.game, id)}
      infiniteScrollLoader={<InfiniteScrollLoader />}
      tab={tab}
      setTab={setTab}
    />
  );
};

export default Fixtures;
